import cx from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Col, Form, Image, Row } from "react-bootstrap";
import Loader from "react-loaders";
import { useCombinedRefs } from "../../hooks";
import { IFXButton } from "./IFXButton";
import { ColorOptions, IFXIcons } from "./IFXIcons";
import { IFXTooltip } from "./IFXTooltip";

const DEFAULT_PROPS = {
  className: "ifx-file-upload form-control form-control-sm p-0 pl-2",
  size: "sm",
  autoComplete: "off",
  type: "file",
};

const NO_FILE_CHOSEN = "No file chosen";
export const IFXFile = forwardRef(
  (
    {
      required,
      selectedFiles = [],
      clearOnChange = false,
      showUploadProgress = false,
      uploadInProgressError = {},
      value,
      removeFile,
      clearTrigger,
      ...props
    },
    ref
  ) => {
    const combinedRef = useCombinedRefs(ref);
    const divRef = React.useRef();
    const _props = { ...DEFAULT_PROPS, className: "", ...props };
    const { multiple, onChange } = _props;

    const [displayMessage, setDisplayMessage] = useState(NO_FILE_CHOSEN);

    useEffect(() => {
      if (multiple)
        if (selectedFiles.length) {
          setDisplayMessage(
            `${selectedFiles.length} ${
              selectedFiles.length == 1 ? "file" : "files"
            } chosen`
          );
        } else {
          setDisplayMessage(NO_FILE_CHOSEN);
        }
    }, [multiple, selectedFiles]);

    const _onChange = useCallback(
      e => {
        // console.log("_onChange", e);
        // console.log("_onChange", e.target.files);

        const files = Array.from(e.target.files);

        if (!multiple) {
          // console.log("_onChange files", files);
          setDisplayMessage(files[0]?.name || NO_FILE_CHOSEN);
        }

        if (onChange) {
          if (clearOnChange || multiple) {
            combinedRef.current.value = "";
          }
          onChange(e, files);
        }
      },
      [onChange]
    );

    const _removeFile = useCallback(
      file => {
        console.log("removeFile::", file);
        if (removeFile) {
          removeFile(file);
        }
      },
      [removeFile]
    );

    const onChooseFile = useCallback(e => {
      // console.log(e);
      divRef.current.focus();
      combinedRef.current.click();
    }, []);

    const onKeyPress = useCallback(e => {
      e.stopPropagation();
      e.preventDefault();
      if (e.which === 13) combinedRef.current.click();
    }, []);

    const clear = useCallback(() => {
      combinedRef.current.value = "";
      const event = new Event("change", { bubbles: true });
      event.simulated = true;
      combinedRef.current.dispatchEvent(event);
    }, []);

    useEffect(() => {
      if (clearTrigger) {
        const files = Array.from(combinedRef.current.files);
        if (files.length) clear();
      }
    }, [clearTrigger]);

    return (
      <Row>
        <Col>
          <div
            tabIndex="0"
            ref={divRef}
            className={cx(DEFAULT_PROPS.className, _props.className, {
              "field-mandatory": required,
            })}
            onClick={onChooseFile}
            onKeyPress={onKeyPress}
          >
            <IFXButton
              className="ifx-file-choose-btn"
              color="secondary"
              name={`Choose ${multiple ? "Files" : "File"}`}
              onClick={() => {}}
              onKeyPress={onKeyPress}
            />
            <div className="ifx-file-chosen-label p-1 pl-2">
              <span>{displayMessage}</span>
            </div>
          </div>
          <Form.Control
            {..._props}
            ref={combinedRef}
            onChange={_onChange}
            hidden
          />
          {multiple && selectedFiles?.length ? (
            <IFXMultiFileView
              selectedFiles={selectedFiles}
              removeFile={_removeFile}
              showUploadProgress={showUploadProgress}
              uploadInProgressError={uploadInProgressError}
            />
          ) : null}
        </Col>
      </Row>
    );
  }
);

const IFXMultiFileView = ({
  selectedFiles,
  removeFile,
  showUploadProgress,
  uploadInProgressError: {
    isInvalid,
    errorMessage = "Upload in progress, please wait.",
  },
}) => {
  return (
    <>
      {showUploadProgress && (
        <div className={`${(isInvalid && "is-invalid") || ""}`}>
          <Form.Control.Feedback type="d-flex ifx-field-error invalid">
            {errorMessage}
          </Form.Control.Feedback>
        </div>
      )}
      <div className="mt-2 d-flex flex-wrap justify-content-start">
        {selectedFiles.map(fileDTO => (
          <IFXFileToken
            {...fileDTO}
            key={fileDTO.file.name}
            removeFile={removeFile}
            showUploadProgress={showUploadProgress}
          />
        ))}
      </div>
    </>
  );
};

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".bmp",
  ".gif",
  ".svg",
  ".tiff",
];

const IFXFileToken = props => {
  const {
    file,
    removeFile,
    showUploadProgress = false,
    uploaded = false,
    uploadProgress = 0,
    failed = false,
    errorMsg,
  } = props;
  const extension = file.name.substring(file.name.lastIndexOf("."));
  const fileName = file.name.substring(0, file.name.lastIndexOf("."));

  const urlImage = useMemo(
    () =>
      IMAGE_EXTENSIONS.includes(extension.toLowerCase())
        ? URL.createObjectURL(file)
        : null,
    [file, extension]
  );

  return (
    <div className="ifx-file-token-wrapper mr-1 mb-1">
      <div className="ifx-file-token">
        <div className="d-flex">
          <IFXTooltip
            placement="top"
            content={
              <div className="d-block">
                {urlImage && !failed ? (
                  <Col xs="12" className="p-0">
                    <Image
                      src={urlImage}
                      style={{ maxWidth: "15rem", maxHeight: "15rem" }}
                    />
                  </Col>
                ) : null}
                <Col xs="12" className="p-0">
                  <span>{file.name}</span>
                </Col>
              </div>
            }
          >
            <div className="d-flex">
              <span className="label"> {fileName}</span>
              <span className="extension">{extension}</span>
            </div>
          </IFXTooltip>
          {showUploadProgress ? (
            <span className="upload-status-icon mt-1 ml-2">
              {uploaded && !failed ? (
                <IFXTooltip placement="top" content="Uploaded Successfully">
                  <div>
                    <span>
                      <IFXIcons
                        iconSource="svg"
                        icon="check"
                        color="success"
                        size="xs"
                      />
                    </span>
                  </div>
                </IFXTooltip>
              ) : uploadProgress > 0 && uploadProgress <= 100 ? (
                <IFXTooltip placement="top" content="Uploading...">
                  <div className="uploading-icon">
                    <Loader
                      className="ifx-processing-icon"
                      type="ball-clip-rotate-multiple"
                      active
                    />
                  </div>
                </IFXTooltip>
              ) : failed && errorMsg ? (
                <IFXTooltip placement="top" content={errorMsg}>
                  <div>
                    <IFXIcons
                      iconSource="svg"
                      icon="error"
                      color={ColorOptions.danger}
                      size="xs"
                    />
                  </div>
                </IFXTooltip>
              ) : (
                <IFXTooltip placement="top" content="Waiting to upload">
                  <div>
                    <IFXIcons
                      iconSource="svg"
                      icon="upload1"
                      color={ColorOptions.ifxBrand}
                      size="xs"
                    />
                  </div>
                </IFXTooltip>
              )}
            </span>
          ) : null}
          <IFXTooltip placement="top" content="Remove">
            <button
              type="button"
              className="token-close"
              onClick={() => removeFile(file)}
            >
              <span aria-hidden="true">×</span>
              <span className="sr-only">Close</span>
            </button>
          </IFXTooltip>
        </div>
        {showUploadProgress &&
        !uploaded &&
        uploadProgress > 0 &&
        uploadProgress <= 100 ? (
          <div className="upload-progress-bar">
            <div
              style={{
                width: `${uploadProgress}%`,
              }}
            ></div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
